import React, { useEffect, useState } from "react";
import axios from "axios";
import { useReveal } from "../lib/hooks";
import { resolveImageUrl } from "../lib/adminApi";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AlliesSection — "Con quienes hemos diseñado"
 * Logos en monocromo por defecto, color al pasar el mouse.
 * Aparece después de la sección "Una inversión blindada" en la portada.
 */
export default function AlliesSection() {
  const [allies, setAllies] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/allies`)
      .then((r) => !cancelled && setAllies(r.data || []))
      .catch(() => !cancelled && setAllies([]))
      .finally(() => !cancelled && setLoaded(true));
    return () => {
      cancelled = true;
    };
  }, []);

  const head = useReveal();
  const body = useReveal();
  const grid = useReveal({ threshold: 0.1 });

  // Don't render the section at all if there are no allies yet (keeps Home clean)
  if (loaded && allies.length === 0) return null;

  return (
    <section
      data-testid="allies-section"
      className="relative border-t border-[var(--pc-line)] bg-[var(--pc-bg)]"
    >
      <div className="pc-container section-pad-y">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          {/* Left column: heading */}
          <div className="lg:col-span-4">
            <div
              className="text-[var(--pc-bronze)]"
              style={{
                fontFamily: "Inter, sans-serif",
                fontWeight: 400,
                fontSize: 11,
                letterSpacing: "0.34em",
                textTransform: "uppercase",
              }}
            >
              Aliados
            </div>
            <div ref={head.ref} className={`mt-5 reveal ${head.revealed ? "is-in" : ""}`}>
              <h2
                className="headline-lg"
                style={{
                  fontSize: "clamp(22px, 2.2vw, 31px)",
                  maxWidth: 380,
                  letterSpacing: "0.12em",
                  lineHeight: 1.22,
                }}
              >
                Con quienes hemos diseñado.
              </h2>
            </div>
            <p
              ref={body.ref}
              className={`body-soft mt-7 max-w-[380px] reveal delay-1 ${body.revealed ? "is-in" : ""}`}
              style={{ fontSize: 15 }}
            >
              Marcas, desarrolladoras y operadores que han confiado en nuestra
              manera de hacer arquitectura.
            </p>
          </div>

          {/* Right column: logo row (single row of 4-6 logos, full color) */}
          <div
            ref={grid.ref}
            data-testid="allies-grid"
            className={`lg:col-span-8 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10 items-center reveal ${
              grid.revealed ? "is-in" : ""
            }`}
          >
            {allies.map((a) => (
              <AllyLogo key={a.id} ally={a} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function AllyLogo({ ally }) {
  // Vista Azul ships as a dark line-art logo and is invisible on the dark background;
  // we invert it to white so it reads. All other logos display in their original colors.
  const needsWhitening = /vista\s*azul/i.test(ally.name || "");
  const inner = (
    <div
      className="ally-cell relative flex items-center justify-center px-3 py-4"
      style={{ minHeight: 180 }}
      title={ally.name}
    >
      <img
        src={resolveImageUrl(ally.logo)}
        alt={ally.name}
        className={`ally-logo ${needsWhitening ? "ally-logo--invert" : ""} max-h-32 md:max-h-36 lg:max-h-40 max-w-full object-contain`}
        draggable="false"
      />
    </div>
  );
  return ally.url ? (
    <a
      href={ally.url}
      target="_blank"
      rel="noreferrer"
      data-testid={`ally-${ally.id}`}
      className="block group"
    >
      {inner}
    </a>
  ) : (
    <div data-testid={`ally-${ally.id}`} className="group">{inner}</div>
  );
}
